import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { AGENTIC_AI_ROUTE } from "@/components/agenticAI/constants";
import {
  DUMMY_CHAT_HISTORY,
  findDummyConversation
} from "@/components/agenticAI/dummyConversation";

/**
 * `resetWorkspace` empties the map for the conversation being left. It runs each
 * time the thread changes, whatever changed the URL: a chat handler, the app
 * sidebar, Back or Forward. A conversation swap is not a route change, so
 * `useAgenticWorkspaceSwap` never fires and the previous conversation's layers,
 * areas and results would stay painted under the new one.
 */
export const useAgenticAI = (resetWorkspace: () => void) => {
  const navigate = useNavigate();
  const { search } = useLocation();

  const { chatId } = useParams();

  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isMapFullScreen, setIsMapFullScreen] = useState(false);
  const [draftThreadId, setDraftThreadId] = useState(() => crypto.randomUUID());
  const [previousChatId, setPreviousChatId] = useState(chatId);

  if (previousChatId !== chatId) {
    setPreviousChatId(chatId);
    if (!chatId) setDraftThreadId(crypto.randomUUID());
  }

  const isAgentChatOpen = !!chatId;
  const agentThreadId = chatId ?? draftThreadId;
  const activeConversation = findDummyConversation(agentThreadId);

  // The first run is skipped: `useAgenticWorkspaceSwap` resets the page load,
  // before the SDK restores the thread.
  const previousThreadIdRef = useRef(agentThreadId);
  useEffect(() => {
    if (previousThreadIdRef.current === agentThreadId) return;
    previousThreadIdRef.current = agentThreadId;
    resetWorkspace();
  }, [agentThreadId, resetWorkspace]);

  const goToChat = (nextChatId?: string) => {
    const chatPath = nextChatId
      ? `${AGENTIC_AI_ROUTE}/${encodeURIComponent(nextChatId)}`
      : AGENTIC_AI_ROUTE;
    navigate(`${chatPath}${search}`, { replace: true });
  };

  const handleToggleSidebar = () => setIsSidebarOpen((isOpen) => !isOpen);

  const handleToggleMapFullScreen = () =>
    setIsMapFullScreen((isExpanded) => !isExpanded);

  const handleNewChat = () => {
    setIsMapFullScreen(false);
    setIsSidebarOpen(true);
    goToChat();
  };

  const handleOpenAgentChat = () => {
    setIsSidebarOpen(false);
    goToChat(agentThreadId);
  };

  const handleSelectChat = (selectedChatId: string) => {
    setIsMapFullScreen(false);
    goToChat(selectedChatId);
  };

  return {
    activeChatId: activeConversation?.id ?? null,
    activeConversation,
    agentThreadId,
    chatHistory: DUMMY_CHAT_HISTORY,
    isAgentChatOpen,
    isMapFullScreen,
    isSidebarOpen,
    handleNewChat,
    handleOpenAgentChat,
    handleSelectChat,
    handleToggleMapFullScreen,
    handleToggleSidebar
  };
};
